import React, { useEffect, useState, useContext, useCallback } from 'react';
import { ScoreGroupConfigButton } from './ScoreGroupConfigButton';
import { ScoreSettings } from './ScoreSettings';
import ScoreGroupsDropDown from './ScoreGroupsDropDown';
import { generalSetupContext } from '../SetupDashboard';
import { EntityStateDTO, ScoreDefinitionDTO } from 'business/models/Models';
import { cloneDeep } from 'lodash';
import WellService from 'business/services/WellService';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { getNewId } from 'utils/Global';
import { RMModal } from 'components/modal';
import { ModalDetails } from 'business/models/CustomModels';

export const WellScoreConfiguration = () => {
  const { scoresList, setScoresList, scoresGroupsList } = useContext(generalSetupContext);
  const [scoreGroupId, setScoreGroupId] = useState<number>(
    scoresGroupsList[0] ? scoresGroupsList[0].scoreGroupsId : -1,
  );
  const [selectedListIndex, setSelectedListIndex] = useState<number>(-1);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    noButtonText: undefined,
    showButtons: true,
  });

  useEffect(() => {
    if (
      !(
        scoreGroupId &&
        scoresGroupsList.some(
          (scoreGroup: { scoreGroupsId: number }) =>
            scoreGroup.scoreGroupsId == scoreGroupId,
        )
      )
    ) {
      setScoreGroupId(scoresGroupsList[0]?.scoreGroupsId);
    } else if (scoreGroupId > 0) {
      setScoreGroupId(scoreGroupId);
    }
    // It's to select first score group by default.
  }, [scoresGroupsList, scoreGroupId]);

  const getScoreListIndex = useCallback(
    (index: number): number => {
      const filteredScoresList = scoresList.filter(
        (score) => score.wellDropScoreGroupId === scoreGroupId,
      );
      const newindex = scoresList.findIndex(
        (score: ScoreDefinitionDTO) => score == filteredScoresList[index],
      );
      return newindex;
    },
    [scoresList, scoreGroupId],
  );

  const isNameDuplicate = useCallback(
    (index: number, value: string): boolean => {
      const tempList = [...scoresList];
      const listIndex = getScoreListIndex(index);
      const isDuplicateValue = tempList.some(
        (item, itemIndex) =>
          item.scoreName.trim().toLowerCase() === value &&
          itemIndex !== listIndex &&
          item.wellDropScoreGroupId == scoreGroupId,
      );
      if (isDuplicateValue) {
        setModalDetails({
          ...modalDetails,
          isOkOnly: true,
          showModal: true,
          onOk: () => setModalDetails({ ...modalDetails, showModal: false }),
          body: 'Please provide a unique name.',
          heading: 'Warning',
          okButtonText: 'Ok',
        });
        return true;
      }
      return false;
    },
    [scoresList, scoreGroupId],
  );

  const addNewScore = useCallback((): void => {
    const selectedScoreGroupIndex = scoresGroupsList.findIndex(
      (scoreGroup) => scoreGroup.scoreGroupsId == scoreGroupId,
    );
    const newItem = new ScoreDefinitionDTO({
      color: '0, 0, 0',
      hit: false,
      hotKey: '',
      isAutoScore:
        selectedScoreGroupIndex >= 0
          ? scoresGroupsList[selectedScoreGroupIndex].isAutoScore
          : false,
      isoValue: 0,
      orderNumber:
        Math.max(
          ...scoresList.map((score) => {
            return score.orderNumber;
          }),
        ) + 1,
      scoreGroupName: '',
      scoreName: '',
      value: 0,
      wellDropScoreGroupId: scoreGroupId,
      wellDropScoreId: getNewId(),
      entityState: EntityStateDTO.New,
    });
    if (isNameDuplicate(scoresList.length, newItem.scoreName)) {
      return;
    }
    const tempScoresList = cloneDeep([...scoresList]);
    const newList = tempScoresList.concat(newItem);
    setScoresList(newList);
    setSelectedRowIndex(
      scoresList.filter((value) => value.wellDropScoreGroupId == scoreGroupId).length,
    );
    setSelectedListIndex(tempScoresList.length);
  }, [scoresList, scoreGroupId]);

  const deleteScore = useCallback(async (): Promise<void> => {
    if (selectedRowIndex === -1) {
      return;
    }

    const wellservice = new WellService();
    if (scoresList[selectedRowIndex]?.wellDropScoreId > 0) {
      const isInUse = await wellservice.isWellScoreInUse(scoresList[selectedListIndex]);
      if (isInUse) {
        setModalDetails({
          ...modalDetails,
          isOkOnly: true,
          showModal: true,
          onOk: () => setModalDetails({ ...modalDetails, showModal: false }),
          body: 'Score is in use.',
          heading: 'Warning',
          okButtonText: 'Ok',
        });
        return;
      }
    }
    let tempScoresList = [...scoresList];
    tempScoresList = tempScoresList.filter((score, index) => index !== selectedListIndex);
    setScoresList(tempScoresList);
    setSelectedListIndex(-1);
    setSelectedRowIndex(-1);
  }, [scoresList, selectedRowIndex]);

  return (
    <>
      <div id='wellscoreconfiguration-wrapper' className="border-box ml-1 mt-2 float-left well-score-container">
        <label id='wellscoreconfiguration-label' className="mt-2 ml-2 font-weight-bold">Well Score Configuration</label>
        <div id='wellscoreconfiguration-button-container' className="scores-buttons-container">
          <label id='wellscoreconfiguration-label-select-score'>Select Score Group</label>
          <div id='wellscoreconfiguration-scoregroup-wrapper' className="scores-group-general">
            <ScoreGroupsDropDown
              dropdownHeight={'100%'}
              dropdownwidth={'100%'}
              defaultText={
                scoresGroupsList.filter(
                  (scoreGroup) => scoreGroup.scoreGroupsId === scoreGroupId,
                )[0]?.name ?? ''
              }
              optionsList={scoresGroupsList}
              onSelectOption={setScoreGroupId}
            ></ScoreGroupsDropDown>
          </div>
          <div id='wellscoreconfiguration-scores-button' className="scores-buttons">
            <div
              id='wellscoreconfiguration-delete-score'
              className="float-right pt-2 cursor-pointer"
              onClick={(e) => deleteScore()}
            >
              <RMSvgIcon
                id='wellscoreconfiguration-svg-remove-icon'
                name="Remove"
                width={'28px'}
                height={'28px'}
                opacity={selectedRowIndex === -1 ? 0.5 : 1}
                fill="#232323"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
            <div
              id='wellscoreconfiguration-add-score'
              className="float-right pt-2 pr-2 cursor-pointer"
              onClick={(e) => addNewScore()}
            >
              <RMSvgIcon
                id='wellscoreconfiguration-svg-add-icon'
                name="Add"
                width={'28px'}
                height={'28px'}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
            <div
              id='wellscoreconfiguration-settings'
              className="float-right pt-2 pr-2 cursor-pointer"
              onClick={(e) => setShowModal(true)}
            >
              <RMSvgIcon
                id='wellscoreconfiguration-svg-settings-icon'
                name="Settings"
                width={'28px'}
                height={'28px'}
                fill="#474747"
                subfolder="Setup"
              ></RMSvgIcon>
            </div>
          </div>
          <div>
            <ScoreGroupConfigButton
              showModal={showModal}
              setShowModal={setShowModal}
            ></ScoreGroupConfigButton>
          </div>
        </div>
        <div id='wellscoreconfiguration-score-settings' className="w-100 float-left setup-score-table-parent-height">
          <ScoreSettings
            scoreGroupId={scoreGroupId}
            selectedRowIndex={selectedRowIndex}
            setSelectedRowIndex={setSelectedRowIndex}
            setSelectedListIndex={setSelectedListIndex}
            getScoreListIndex={getScoreListIndex}
            isNameDuplicate={isNameDuplicate}
          ></ScoreSettings>
        </div>
      </div>
      <RMModal
        {...modalDetails}
        onHide={() => setModalDetails({ ...modalDetails, showModal: false })}
      />
    </>
  );
};
